import React from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBooks, faCube, faFileVector, faFilter, faFolder, faPanorama, faPlay } from '@fortawesome/pro-solid-svg-icons'
import { assetHref, assetVersionHref, nodeHref } from './record-links'
import { nodeNoun } from './entry-story'

// A course or folder in an entry's headline (owner 2026-10-09): the admin's
// own icon for its kind and its name, a link to its page while it is
// current. Deleted ones (gone, or in the recycle bin) and ones that moved
// to another organization are plain text with a note, under the names the
// trail recorded. A super admin's entry may be another organization's, so
// for them it is text too (as recordView's courses).
//
// onWithin(place): the chip also offers "<name> and everything in it" (the
// within filter), as a real button beside the name.

const ICONS = { course: faPanorama, video: faPlay, folder: faFolder, template: faFileVector, library: faBooks }
export const placeIcon = type => ICONS[nodeNoun(type)] || faCube

const NOTES = { deleted: 'Deleted', recycled: 'In the recycle bin', moved: 'Moved to another organization' }
// the chip's own word for it (its title says it in full)
const SHORT = { deleted: 'deleted', recycled: 'deleted', moved: 'moved' }

export const placeView = (place, { sys = false } = {}) => {
  if (!place) return null
  const status = place.status || 'current'
  const name = (place.name || '').trim() || `${nodeNoun(place.type)[0].toUpperCase()}${nodeNoun(place.type).slice(1)} ${place.id}`
  return {
    name,
    href: status === 'current' && !sys ? nodeHref(place.type, place.id) : null,
    note: NOTES[status] || null,
    short: SHORT[status] || null,
    deleted: Boolean(place.deleted) || status === 'deleted' || status === 'recycled',
    // a node that left this organization has no tree here to narrow to
    within: Boolean(place.id) && status !== 'moved',
    icon: placeIcon(place.type)
  }
}

export const withinText = name => `${name} and everything in it`

// A Details pivot's words (owner 2026-10-09: "EducationXR Interactable
// Showcase and everything in it" wrapped its pill onto two lines, "crazy
// big"): the entry's own course or folder is "Everything in this course";
// another is named ("Everything in Showcases") and a long name is cut short
// inside the one-line pill. The pill's title is withinText, in full.
export const withinPivotText = (place, { own = false } = {}) =>
  own ? `Everything in this ${nodeNoun(place.type)}` : `Everything in ${placeView(place).name}`

export const PlaceChip = ({ place, sys = false, onWithin }) => {
  const v = placeView(place, { sys })
  if (!v) return null
  const body = <><FontAwesomeIcon icon={v.icon} className="au-place-icon" aria-hidden="true" /><span className="au-place-name">{v.name}</span></>
  return (
    <span className={`au-place${v.deleted ? ' au-place-gone' : ''}`} title={v.note || undefined}>
      {v.href ? <Link className="au-place-link" to={v.href}>{body}</Link> : <span className="au-place-link">{body}</span>}
      {v.short && <span className="au-place-note">{v.short}</span>}
      {onWithin && v.within &&
        <button type="button" className="au-place-within" aria-label={withinText(v.name)} title={withinText(v.name)} onClick={() => onWithin(place)}>
          <FontAwesomeIcon icon={faFilter} aria-hidden="true" />
        </button>}
    </span>
  )
}

// A headline's file and its version (owner 2026-10-09: "it would be nice
// if v4 went to the version details page and macOS linked to the mac os
// asset"): the file's name opens its versions, the version pill that
// version's own page, both under the course's page. By the course chip's
// rules: a link only while the course, the file and the version are all
// current, and never for a super admin (another organization's pages). The
// outermost record that's gone says so -- the course's chip for its files,
// the file for its versions, else the version -- with the chip's word and
// title; `said`: the headline's verb already says it, so only the title.
// file: entry-story.js fileOf.
const statusOf = s => s || 'current'
export const fileView = (file, { sys = false, version = false } = {}) => {
  const f = file || {}
  const place = f.place
  const placeHere = Boolean(place) && statusOf(place.status) === 'current' && !place.deleted
  const asset = statusOf(f.asset)
  const own = version ? statusOf(f.version) : asset
  // a gone course or (for a version) a gone file already carries the mark
  const above = !placeHere || (version && asset !== 'current')
  const mark = !above && own !== 'current' ? own : null
  const open = !sys && placeHere && asset === 'current' && own === 'current'
  const href = !open ? null : version ? assetVersionHref(place.type, place.id, f.assetID, f.versionID) : assetHref(place.type, place.id, f.assetID)
  return { href, note: NOTES[mark] || null, short: SHORT[mark] || null, gone: Boolean(mark) }
}

// A file's name in a headline ("the [macOS build] for ...").
export const FileName = ({ text, file, said = false, sys = false }) => {
  const v = file ? fileView(file, { sys }) : { href: null }
  if (v.href) return <Link className="au-file" to={v.href}>{text}</Link>
  return (
    <>
      <b className={v.gone ? 'au-file-gone' : undefined} title={v.note || undefined}>{text}</b>
      {v.short && !said && <>{' '}<span className="au-file-note">{`(${v.short})`}</span></>}
    </>
  )
}

// A version's pill ("v14"): a link to the version's own page while it can
// be opened, named with its file ("macOS build v14").
export const VersionBadge = ({ n, file, said = false, sys = false }) => {
  const text = `v${n}`
  const v = file ? fileView(file, { sys, version: true }) : { href: null }
  const name = file && file.name ? `${file.name} ${text}` : undefined
  if (v.href) return <Link className="au-ver" to={v.href} aria-label={name} title={name}>{text}</Link>
  return (
    <span className={`au-ver${v.gone ? ' au-ver-gone' : ''}`} title={v.note || undefined}>
      {text}
      {v.short && !said && <span className="au-ver-note">{v.short}</span>}
    </span>
  )
}

// A headline's segments (entry-story.js) as the line after the person's
// name.
export const Headline = ({ segments, sys = false, onWithin }) =>
  <span className="au-headline">
    {(segments || []).map((s, i) => {
      if (typeof s === 'string') return <React.Fragment key={i}>{s}</React.Fragment>
      if (s.strong) return <FileName key={i} text={s.strong} file={s.file} said={s.said} sys={sys} />
      if (s.version !== undefined) return <VersionBadge key={i} n={s.version} file={s.file} said={s.said} sys={sys} />
      if (s.place) return <PlaceChip key={i} place={s.place} sys={sys} onWithin={onWithin} />
      return null
    })}
  </span>

// A story's chips (entry-story.js chipsOf): current v13 → v14, the upload
// and its size, and "no file fingerprint" when a finished build saved none.
export const FactChips = ({ chips }) =>
  chips && chips.length > 0
    ? <div className="au-facts">
        {chips.map(c =>
          <span key={c.key} className="au-fact" title={c.title || undefined}>
            {c.key === 'current'
              ? <>current{c.from ? <> <del>{`v${c.from}`}</del> →</> : null} <b>{`v${c.to}`}</b></>
              : c.key === 'upload'
                ? <>{c.text.replace(/\s+\S+$/, '')} <b className={`au-fact-word${c.tone ? ` au-tone-${c.tone}` : ''}`}>{c.text.split(' ').pop()}</b>{c.size ? ` · ${c.size}` : ''}</>
                : c.tone
                  ? <b className={`au-fact-word au-tone-${c.tone}`}>{c.text}</b>
                  : c.text}
          </span>)}
      </div>
    : null
